import { http, type ObjectValue } from '@kunlun/dependencies';

export interface IMatchProductApiRst {
  erpCodeStr: string;
  errorInfos: string[];
  detailUrls: string[];
  retailCategory: {
    code: string;
    id: string;
    name: string;
    parentCateName: string;
  };
  itemSkus: {
    attrs?: { attrName: string; attrValue: string }[];
    erpCode: string;
    meituanSkuId: string;
    barCode: string;
    standardRetailPrice: string;
    weight: string;
    weightUnit: string;
    id: string;
    itemNumber: string;
  }[];
  meituanCategory: {
    meituanId: string;
    id: string;
    meituanNamePath: string;
  };
  picsUrls: string[];
  id: string;
  code: string;
  meituanCategoryId: string;
  name: string;
}

export type variablesType = {
  path: string;
  [key: string]: string;
};

// 安全转义
const escapeGqlString = (str: string | undefined | null): string => {
  if (!str) return '';
  return str.replace(/"/g, '\\"').replace(/\n/g, '\\n').replace(/\r/g, '\\r');
};

// 🔥 核心工具：把数组转成【GraphQL 合法对象格式】（无引号key）
const toGqlObjectList = (list?: { code: string; name: string }[]) => {
  if (!list || list.length === 0) return '';
  return list.map((item) => `{ code: "${escapeGqlString(item.code)}", name: "${escapeGqlString(item.name)}" }`).join(', ');
};
const toGqlErpCodeList = (list?: { erpCode: string }[]) => {
  if (!list || list.length === 0) return '';
  return list.map((item) => `{ erpCode: "${escapeGqlString(item.erpCode)}" }`).join(', ');
};

// 自营商品接口
export const matchProductApi = async (
  params: {
    itemSkus: { erpCode: string }[];
    erpCodeStr: string;
    name: string;
    detailUrls: string[];
    picsUrls: string[];
    meituanCategory: { meituanNamePath: string; meituanId: string; id: string };
    meituanAppSpuCode?: string;
    retailCategory?: { name: string; code: string; id?: string; parentCateName?: string }[];
  },
  variables: ObjectValue
) => {
  const cleanRetailCategory = params.retailCategory?.map((item) => ({ code: item.code, name: item.name }));
  const retailGql = cleanRetailCategory?.length ? `retailCategory: [${toGqlObjectList(cleanRetailCategory)}]` : '';
  const itemSkusGql = params.itemSkus?.length ? `itemSkus: [${toGqlErpCodeList(params.itemSkus)}]` : '';
  const gql = `
    mutation {
      backendRetailItemMutation {
        matchItem(data: {
          erpCodeStr: "${escapeGqlString(params.erpCodeStr)}"
          name: "${escapeGqlString(params.name)}"
          detailUrls: ${JSON.stringify(params.detailUrls || [])}
          picsUrls: ${JSON.stringify(params.picsUrls || [])}
          ${itemSkusGql}
          ${
            params.meituanCategory?.id
              ? `
            meituanCategory: {
              meituanNamePath: "${escapeGqlString(params.meituanCategory.meituanNamePath)}",
              meituanId: "${escapeGqlString(params.meituanCategory.meituanId)}",
              id: "${escapeGqlString(params.meituanCategory.id)}"
            }
          `
              : ''
          }
          ${params.meituanAppSpuCode ? `meituanAppSpuCode: "${escapeGqlString(params.meituanAppSpuCode)}"` : ''}
          ${retailGql}
        }) {
          name
          detailUrls
          meituanAppSpuCode
          itemSkus {  
            materialGroupLevelTwoName
            materialGroupLevelOneName
            materialGroupLevelThreeName
            materialGroupCode
            skuCategoryCode 
            itemNumber 
            erpCode 
            meituanSkuId 
            barCode 
            standardRetailPrice 
            weight weightUnit id attrs { attrName attrValue } }
          meituanCategory { meituanNamePath meituanId id }
          retailCategory { code id name parentCateName }
          errorInfos
          picsUrls
          erpCodeStr
          id
          code
          meituanCategoryId
        }
      }
    }
  `;

  const res = await http.mutate<IMatchProductApiRst>('deliRetail', gql, variables);
  return res.data.backendRetailItemMutation.matchItem;
};

// 非自营商品接口
export const backendNotSelfOperatedItemMutation = async (
  params: {
    barCode: string;
    name: string;
    meituanCategory: { meituanNamePath: string; meituanId: string; id: string };
    meituanAppSpuCode?: string;
    retailCategory?: { name: string; code: string; id?: string; parentCateName?: string }[];
  },
  variables: ObjectValue
) => {
  const cleanRetailCategory = params.retailCategory?.map((item) => ({ code: item.code, name: item.name }));
  const retailGql = cleanRetailCategory?.length ? `retailCategory: [${toGqlObjectList(cleanRetailCategory)}]` : '';

  const gql = `
    mutation {
      backendNotSelfOperatedItemMutation {
        matchItem(data: {
          barCode: "${escapeGqlString(params.barCode)}"
          name: "${escapeGqlString(params.name)}"
          ${
            params.meituanCategory?.id
              ? `
            meituanCategory: {
              meituanNamePath: "${escapeGqlString(params.meituanCategory.meituanNamePath)}",
              meituanId: "${escapeGqlString(params.meituanCategory.meituanId)}",
              id: "${escapeGqlString(params.meituanCategory.id)}",
              ${params.meituanAppSpuCode ? `meituanAppSpuCode: "${escapeGqlString(params.meituanAppSpuCode)}"` : ''}
              ${retailGql}
            }
          `
              : ''
          }
        }) {
          itemSkus { skuCategoryCode standardRetailPrice barCode id attrs { attrName attrValue } }
          picsUrls
          name
          errorInfos
          meituanCategory { meituanNamePath meituanId id }
          retailCategory { code id name parentCateName }
          barCode
          id
          code
          meituanCategoryId
          meituanAppSpuCode
        }
      }
    }
  `;

  const res = await http.mutate<IMatchProductApiRst>('deliRetail', gql, variables);
  return res.data.backendNotSelfOperatedItemMutation.matchItem;
};
